import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormGroup,
  Checkbox,
  InputAdornment
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Assignment as ReportIcon,
  LocalOffer as ExemptionIcon,
  DateRange as DateRangeIcon,
  Cloud as CloudIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  AccountBalance as AccountIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface TaxType {
  name: string;
  code: string;
  description?: string;
  rate: number;
  type: 'percentage' | 'fixed';
  category: string;
  applicableTo: string;
  specificItems?: string[];
  isCompound: boolean;
  priority: number;
  isActive: boolean;
}

interface TaxExemption {
  name: string;
  code: string;
  description?: string;
  type: string;
  appliesTo: string[];
  requiresDocumentation: boolean;
  documentationRequired?: string;
  isActive: boolean;
}

interface TaxHoliday {
  name: string;
  startDate: Date;
  endDate: Date;
  affectedTaxTypes: string[];
  reductionType: 'exempt' | 'reduced';
  reductionPercentage?: number;
  applicableTo: string;
  isActive: boolean;
}

interface TaxConfiguration {
  _id?: string;
  locationId: string;
  taxInclusivePricing: boolean;
  taxTypes: TaxType[];
  exemptions: TaxExemption[];
  taxHolidays: TaxHoliday[];
  integrations: {
    provider: string;
    apiKey?: string;
    companyCode?: string;
    isActive: boolean;
    validateAddresses: boolean;
    autoCalculate: boolean;
  };
  reporting: {
    frequency: string;
    filingDeadlineDay: number;
    autoGenerateReports: boolean;
    reportRecipients: { email: string; name: string }[];
    includeInFinancialReports: boolean;
  };
  compliance: {
    taxId?: string;
    registrationNumber?: string;
    nexusStates: { state: string; registrationNumber: string; effectiveDate: Date }[];
    lastAuditDate?: Date;
    nextAuditDate?: Date;
  };
}

const TaxManagementPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locations, setLocations] = useState<any[]>([]);
  const [config, setConfig] = useState<TaxConfiguration | null>(null);
  const [taxSummary, setTaxSummary] = useState<any>(null);
  const [reports, setReports] = useState<any[]>([]);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  
  // Dialog states
  const [taxTypeDialog, setTaxTypeDialog] = useState(false);
  const [exemptionDialog, setExemptionDialog] = useState(false);
  const [holidayDialog, setHolidayDialog] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [editingTaxType, setEditingTaxType] = useState<TaxType | null>(null);
  const [editingExemption, setEditingExemption] = useState<TaxExemption | null>(null);
  const [editingHoliday, setEditingHoliday] = useState<TaxHoliday | null>(null);
  
  // Report generation
  const [reportPeriod, setReportPeriod] = useState('monthly');
  const [reportStartDate, setReportStartDate] = useState<Date>(new Date());
  const [reportEndDate, setReportEndDate] = useState<Date>(new Date());
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      loadTaxConfiguration();
      loadTaxSummary();
      loadTaxReports();
    }
  }, [selectedLocation]);

  const loadLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLocations(response.data);
      if (response.data.length > 0) {
        setSelectedLocation(response.data[0]._id);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadTaxConfiguration = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/tax/configuration/${selectedLocation}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setConfig(response.data);
      validateConfiguration(response.data);
    } catch (error) {
      console.error('Error loading tax configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTaxSummary = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1);
      
      const response = await axios.get(`${API_BASE_URL}/tax/summary/${selectedLocation}`, {
        params: { startDate, endDate },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTaxSummary(response.data);
    } catch (error) {
      console.error('Error loading tax summary:', error);
    }
  };

  const loadTaxReports = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/tax/reports/${selectedLocation}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error loading tax reports:', error);
    }
  };

  const validateConfiguration = async (config: TaxConfiguration) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/tax/configuration/${selectedLocation}/validate`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setValidationIssues(response.data.issues || []);
    } catch (error) {
      console.error('Error validating configuration:', error);
    }
  };

  const saveTaxConfiguration = async () => {
    if (!config) return;
    
    try {
      setSaving(true);
      await axios.put(
        `${API_BASE_URL}/tax/configuration/${selectedLocation}`,
        config,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      alert('Tax configuration saved successfully');
      loadTaxConfiguration();
    } catch (error) {
      console.error('Error saving tax configuration:', error);
      alert('Failed to save tax configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTaxType = () => {
    setEditingTaxType({
      name: '',
      code: '',
      description: '',
      rate: 0,
      type: 'percentage',
      category: 'sales_tax',
      applicableTo: 'all',
      specificItems: [],
      isCompound: false,
      priority: 0,
      isActive: true
    });
    setTaxTypeDialog(true);
  };

  const handleSaveTaxType = () => {
    if (!config || !editingTaxType) return;
    
    const newConfig = { ...config };
    const existingIndex = newConfig.taxTypes.findIndex(t => t.code === editingTaxType.code);
    
    if (existingIndex >= 0) {
      newConfig.taxTypes[existingIndex] = editingTaxType;
    } else {
      newConfig.taxTypes.push(editingTaxType);
    }
    
    setConfig(newConfig);
    setTaxTypeDialog(false);
    setEditingTaxType(null);
  };

  const handleDeleteTaxType = (code: string) => {
    if (!config) return;
    
    if (window.confirm('Are you sure you want to delete this tax type?')) {
      const newConfig = { ...config };
      newConfig.taxTypes = newConfig.taxTypes.filter(t => t.code !== code);
      setConfig(newConfig);
    }
  };

  const generateTaxReport = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/tax/report/generate`,
        {
          locationId: selectedLocation,
          periodType: reportPeriod,
          startDate: reportStartDate,
          endDate: reportEndDate
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      alert('Tax report generated successfully');
      setReportDialog(false);
      loadTaxReports();
    } catch (error) {
      console.error('Error generating tax report:', error);
      alert('Failed to generate tax report');
    }
  };

  const fileTaxReport = async (reportId: string) => {
    const confirmationNumber = prompt('Enter filing confirmation number:');
    if (!confirmationNumber) return;
    
    try {
      await axios.post(
        `${API_BASE_URL}/tax/report/${reportId}/file`,
        {
          confirmationNumber,
          paymentAmount: 0,
          paymentMethod: 'Online',
          notes: 'Filed via admin portal'
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      alert('Tax report filed successfully');
      loadTaxReports();
    } catch (error) {
      console.error('Error filing tax report:', error);
      alert('Failed to file tax report');
    }
  };

  const testIntegration = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/tax/integrations/test`,
        { locationId: selectedLocation },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        alert('Integration test successful!');
      } else {
        alert(`Integration test failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Error testing integration:', error);
      alert('Failed to test integration');
    }
  };

  const taxCategories = [
    'sales_tax',
    'occupancy_tax',
    'resort_fee',
    'local_tax',
    'state_tax',
    'tourism_tax',
    'environmental_fee',
    'other'
  ];

  const exemptionTypes = [
    'government',
    'non_profit',
    'resale',
    'diplomatic',
    'educational',
    'medical',
    'other'
  ];

  const integrationProviders = [
    { value: 'none', label: 'Manual Calculation' },
    { value: 'avalara', label: 'Avalara AvaTax' },
    { value: 'taxjar', label: 'TaxJar' },
    { value: 'vertex', label: 'Vertex' }
  ];

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              Tax Management
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations.map((loc) => (
                  <MenuItem key={loc._id} value={loc._id}>
                    {loc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={saveTaxConfiguration}
                disabled={saving}
              >
                Save Configuration
              </Button>
              <Button
                variant="outlined"
                startIcon={<ReportIcon />}
                onClick={() => setReportDialog(true)}
              >
                Generate Report
              </Button>
            </Box>
          </Grid>

          {validationIssues.length > 0 && (
            <Grid item xs={12}>
              <Alert severity="warning">
                <Typography variant="subtitle2">Configuration Issues:</Typography>
                <ul>
                  {validationIssues.map((issue, index) => (
                    <li key={index}>{issue}</li>
                  ))}
                </ul>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Tax Types" icon={<MoneyIcon />} />
                <Tab label="Exemptions" icon={<ExemptionIcon />} />
                <Tab label="Tax Holidays" icon={<DateRangeIcon />} />
                <Tab label="Reports" icon={<ReportIcon />} />
                <Tab label="Integration" icon={<CloudIcon />} />
                <Tab label="Summary" icon={<AccountIcon />} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                {/* Tax Types Tab */}
                <Box mb={2}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddTaxType}
                  >
                    Add Tax Type
                  </Button>
                </Box>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Code</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Rate</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Applies To</TableCell>
                        <TableCell>Compound</TableCell>
                        <TableCell>Active</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {config?.taxTypes.map((tax) => (
                        <TableRow key={tax.code}>
                          <TableCell>{tax.name}</TableCell>
                          <TableCell>
                            <Chip label={tax.code} size="small" />
                          </TableCell>
                          <TableCell>{tax.category.replace('_', ' ')}</TableCell>
                          <TableCell>
                            {tax.type === 'percentage' ? `${tax.rate}%` : `$${tax.rate}`}
                          </TableCell>
                          <TableCell>{tax.type}</TableCell>
                          <TableCell>{tax.applicableTo}</TableCell>
                          <TableCell>
                            {tax.isCompound ? <CheckIcon color="success" /> : '-'}
                          </TableCell>
                          <TableCell>
                            {tax.isActive ? (
                              <Chip label="Active" color="success" size="small" />
                            ) : (
                              <Chip label="Inactive" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingTaxType(tax);
                                setTaxTypeDialog(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteTaxType(tax.code)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                {/* Exemptions Tab */}
                <Box mb={2}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setExemptionDialog(true)}
                  >
                    Add Exemption
                  </Button>
                </Box>

                <Grid container spacing={2}>
                  {config?.exemptions.map((exemption) => (
                    <Grid item xs={12} md={6} key={exemption.code}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {exemption.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" gutterBottom>
                            Code: {exemption.code} | Type: {exemption.type}
                          </Typography>
                          <Typography variant="body2" gutterBottom>
                            {exemption.description}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="caption">Applies to:</Typography>
                            <Box>
                              {exemption.appliesTo.map((tax) => (
                                <Chip
                                  key={tax}
                                  label={tax}
                                  size="small"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </Box>
                          </Box>
                          {exemption.requiresDocumentation && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              Documentation required: {exemption.documentationRequired}
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                {/* Tax Holidays Tab */}
                <Box mb={2}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setHolidayDialog(true)}
                  >
                    Add Tax Holiday
                  </Button>
                </Box>

                <List>
                  {config?.taxHolidays.map((holiday, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={holiday.name}
                        secondary={`${new Date(holiday.startDate).toLocaleDateString()} - ${new Date(holiday.endDate).toLocaleDateString()} | ${holiday.reductionType === 'exempt' ? 'Tax Exempt' : `${holiday.reductionPercentage}% Reduction`}`}
                      />
                      <ListItemSecondaryAction>
                        <Chip
                          label={holiday.isActive ? 'Active' : 'Inactive'}
                          color={holiday.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                {/* Reports Tab */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Period</TableCell>
                        <TableCell>Date Range</TableCell>
                        <TableCell>Total Sales</TableCell>
                        <TableCell>Tax Collected</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reports.map((report) => (
                        <TableRow key={report._id}>
                          <TableCell>{report.periodType}</TableCell>
                          <TableCell>
                            {new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>${report.summary.totalSales.toFixed(2)}</TableCell>
                          <TableCell>${report.summary.totalTaxCollected.toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={report.filing.status}
                              color={report.filing.status === 'filed' ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => window.open(`${API_BASE_URL}/tax/report/${report._id}`, '_blank')}
                            >
                              View
                            </Button>
                            {report.filing.status !== 'filed' && (
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => fileTaxReport(report._id)}
                              >
                                File
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                {/* Integration Tab */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Tax Provider</InputLabel>
                      <Select
                        value={config?.integrations.provider || 'none'}
                        onChange={(e) => {
                          if (config) {
                            setConfig({
                              ...config,
                              integrations: {
                                ...config.integrations,
                                provider: e.target.value
                              }
                            });
                          }
                        }}
                      >
                        {integrationProviders.map((provider) => (
                          <MenuItem key={provider.value} value={provider.value}>
                            {provider.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {config?.integrations.provider !== 'none' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="API Key"
                          type="password"
                          value={config?.integrations.apiKey || ''}
                          onChange={(e) => {
                            if (config) {
                              setConfig({
                                ...config,
                                integrations: {
                                  ...config.integrations,
                                  apiKey: e.target.value
                                }
                              });
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Company Code"
                          value={config?.integrations.companyCode || ''}
                          onChange={(e) => {
                            if (config) {
                              setConfig({
                                ...config,
                                integrations: {
                                  ...config.integrations,
                                  companyCode: e.target.value
                                }
                              });
                            }
                          }}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config?.integrations.isActive || false}
                                onChange={(e) => {
                                  if (config) {
                                    setConfig({
                                      ...config,
                                      integrations: {
                                        ...config.integrations,
                                        isActive: e.target.checked
                                      }
                                    });
                                  }
                                }}
                              />
                            }
                            label="Integration Active"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config?.integrations.autoCalculate || false}
                                onChange={(e) => {
                                  if (config) {
                                    setConfig({
                                      ...config,
                                      integrations: {
                                        ...config.integrations,
                                        autoCalculate: e.target.checked
                                      }
                                    });
                                  }
                                }}
                              />
                            }
                            label="Auto-calculate Taxes"
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config?.integrations.validateAddresses || false}
                                onChange={(e) => {
                                  if (config) {
                                    setConfig({
                                      ...config,
                                      integrations: {
                                        ...config.integrations,
                                        validateAddresses: e.target.checked
                                      }
                                    });
                                  }
                                }}
                              />
                            }
                            label="Validate Addresses"
                          />
                        </FormGroup>
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          onClick={testIntegration}
                          disabled={!config?.integrations.isActive}
                        >
                          Test Integration
                        </Button>
                      </Grid>
                    </>
                  )}
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={5}>
                {/* Summary Tab */}
                {taxSummary && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Transactions
                          </Typography>
                          <Typography variant="h4">
                            {taxSummary.summary.totalTransactions}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Sales
                          </Typography>
                          <Typography variant="h4">
                            ${taxSummary.summary.totalSales.toFixed(2)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Total Tax Collected
                          </Typography>
                          <Typography variant="h4">
                            ${taxSummary.summary.totalTax.toFixed(2)}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Card>
                        <CardContent>
                          <Typography color="textSecondary" gutterBottom>
                            Effective Tax Rate
                          </Typography>
                          <Typography variant="h4">
                            {((taxSummary.summary.totalTax / taxSummary.summary.totalSales) * 100).toFixed(2)}%
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Tax Breakdown by Type
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Tax Type</TableCell>
                                  <TableCell align="right">Amount Collected</TableCell>
                                  <TableCell align="right">Transactions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {taxSummary.taxByType.map((tax: any) => (
                                  <TableRow key={tax._id}>
                                    <TableCell>{tax.taxName}</TableCell>
                                    <TableCell align="right">
                                      ${tax.totalCollected.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">{tax.transactions}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </TabPanel>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Tax Type Dialog */}
      <Dialog open={taxTypeDialog} onClose={() => setTaxTypeDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTaxType?.code ? 'Edit Tax Type' : 'Add Tax Type'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tax Name"
                value={editingTaxType?.name || ''}
                onChange={(e) => setEditingTaxType({ ...editingTaxType!, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tax Code"
                value={editingTaxType?.code || ''}
                onChange={(e) => setEditingTaxType({ ...editingTaxType!, code: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={editingTaxType?.description || ''}
                onChange={(e) => setEditingTaxType({ ...editingTaxType!, description: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={editingTaxType?.category || 'sales_tax'}
                  onChange={(e) => setEditingTaxType({ ...editingTaxType!, category: e.target.value })}
                >
                  {taxCategories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat.replace('_', ' ')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editingTaxType?.type || 'percentage'}
                  onChange={(e) => setEditingTaxType({ ...editingTaxType!, type: e.target.value as 'percentage' | 'fixed' })}
                >
                  <MenuItem value="percentage">Percentage</MenuItem>
                  <MenuItem value="fixed">Fixed Amount</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={editingTaxType?.type === 'percentage' ? 'Rate (%)' : 'Amount ($)'}
                type="number"
                value={editingTaxType?.rate || 0}
                onChange={(e) => setEditingTaxType({ ...editingTaxType!, rate: parseFloat(e.target.value) || 0 })}
                InputProps={{
                  startAdornment: editingTaxType?.type === 'fixed' ? <InputAdornment position="start">$</InputAdornment> : null,
                  endAdornment: editingTaxType?.type === 'percentage' ? <InputAdornment position="end">%</InputAdornment> : null,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Applies To</InputLabel>
                <Select
                  value={editingTaxType?.applicableTo || 'all'}
                  onChange={(e) => setEditingTaxType({ ...editingTaxType!, applicableTo: e.target.value })}
                >
                  <MenuItem value="all">All Items</MenuItem>
                  <MenuItem value="accommodations">Accommodations Only</MenuItem>
                  <MenuItem value="amenities">Amenities Only</MenuItem>
                  <MenuItem value="store_items">Store Items Only</MenuItem>
                  <MenuItem value="services">Services Only</MenuItem>
                  <MenuItem value="specific_items">Specific Items</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingTaxType?.isCompound || false}
                    onChange={(e) => setEditingTaxType({ ...editingTaxType!, isCompound: e.target.checked })}
                  />
                }
                label="Compound Tax (calculated on subtotal + other taxes)"
              />
            </Grid>
            {editingTaxType?.isCompound && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Priority (order of calculation)"
                  type="number"
                  value={editingTaxType?.priority || 0}
                  onChange={(e) => setEditingTaxType({ ...editingTaxType!, priority: parseInt(e.target.value) || 0 })}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingTaxType?.isActive || true}
                    onChange={(e) => setEditingTaxType({ ...editingTaxType!, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaxTypeDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTaxType} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Report Generation Dialog */}
      <Dialog open={reportDialog} onClose={() => setReportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Tax Report</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Report Period</InputLabel>
                  <Select
                    value={reportPeriod}
                    onChange={(e) => setReportPeriod(e.target.value)}
                  >
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">Quarterly</MenuItem>
                    <MenuItem value="annually">Annual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Start Date"
                  value={reportStartDate}
                  onChange={(date) => date && setReportStartDate(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="End Date"
                  value={reportEndDate}
                  onChange={(date) => date && setReportEndDate(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialog(false)}>Cancel</Button>
          <Button onClick={generateTaxReport} variant="contained">Generate</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TaxManagementPage;